import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { ROUTES } from "../../constants/clientRoutes";
import { ACTION_DISPLAY_TYPES } from "../../constants/enums";
import { SetSelectedRecipient, SetSendMoneyCountryCurrency } from "../../actions/sendMoney";
import useCommitSendMoney from "../../hooks/sendMoney/useCommitSendMoney";

// types
import type { SendMoneyReview } from "../../types/api/sendMoney";

// ui components
import FlowScreen from "../../components/Layouts/FlowScreen";
import SingleColumnLayout from "../../components/Layouts/SingleColumnLayout";
import DisplaySection from "../../components/MoneyTransfer/DisplaySection";
import Action from "../../components/Common/Action";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import SendMoneyFooter from "../../components/SendMoney/SendMoneyFooter";

interface SendMoneyReviewScreenProps {
  review: SendMoneyReview;
  transactionContextId: string;
}

const SendMoneyReviewScreen = ({
  review,
  transactionContextId,
}: SendMoneyReviewScreenProps): React.ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { loading, commitTransfer } = useCommitSendMoney();

  const handleSubmit = useCallback(async () => {
    const result = await commitTransfer({ quoteId: review.quoteId, transactionContextId });
    if (!result) return;

    // Leave the review route before clearing the flow: SendMoneyWorkflow's freshness guard
    // reads quoteId from the store, and clearing it while still mounted here would make
    // the review look superseded and trigger its fallback redirect.
    history.replace(ROUTES.SEND_MONEY.CONFIRMATION, { result });

    // Quote is consumed on success or failure: clear country, currency, operator and quote.
    dispatch(SetSendMoneyCountryCurrency({ country: "", currency: "" }));
    dispatch(SetSelectedRecipient(null));
  }, [commitTransfer, dispatch, history, review.quoteId, transactionContextId]);

  return (
    <FlowScreen flowTitle={review.title} showBackButton className="v3">
      <SingleColumnLayout>
        <div className="bwp-send-money-review">
          <DisplaySection variant="plain" sections={review.sections} className="bwp-display-section-summary" />

          <SendMoneyFooter content={review.footer.content} disclaimers={review.footer.disclaimers} />
        </div>
      </SingleColumnLayout>

      <div className="bwp-fixed-footer">
        <div className="container">
          <SingleColumnLayout>
            <Action
              loading={loading}
              disabled={loading}
              onClick={handleSubmit}
              displayType={ACTION_DISPLAY_TYPES.PRIMARY}
              title={review.buttonText}
            />
          </SingleColumnLayout>
        </div>
      </div>

      <ApiErrorModal />
    </FlowScreen>
  );
};

export default SendMoneyReviewScreen;